import React, { FC, useEffect, useState } from 'react';
import { useGetValuationSettings } from '../../../hooks/api/ValuationSettings/useGetValuationSettings';
import { useUpdateValuationSettings } from '../../../hooks/api/ValuationSettings/useUpdateValuationSettings';
import {
  DEFAULT_MARKET_VALUE_RATE,
  MAX_MARKET_VALUE_RATE,
  MIN_MARKET_VALUE_RATE,
  formatMarketValueRate,
  parseMarketValueRate,
} from '../../../utility/valuationSettings';
import { classNames } from '../../../utility/utility';

const ValuationSettingsPage: FC = () => {
  const { data: valuationSettings, isLoading } = useGetValuationSettings();
  const { mutateAsync: updateValuationSettings, isPending: isSaving, error: saveError } = useUpdateValuationSettings();

  const [ rateInput, setRateInput ] = useState('');
  const [ saved, setSaved ] = useState(false);

  useEffect(() => {
    if (valuationSettings) {
      setRateInput(String(valuationSettings.market_value_rate));
    }
  }, [ valuationSettings ]);

  const parsedRate = parseMarketValueRate(rateInput);
  const isInvalid = rateInput !== '' && parsedRate === null;
  const exampleValue = 400000 * (1 + (parsedRate ?? 0) / 100);

  const handleChange = (value: string) => {
    setRateInput(value);
    setSaved(false);
  };

  const handleSave = async () => {
    if (parsedRate === null) return;
    await updateValuationSettings({ market_value_rate: parsedRate });
    setSaved(true);
  };

  return (
    <div className="px-2">
      <h1 className="text-base font-semibold leading-6 text-gray-900">Property Valuations</h1>
      <p className="mt-2 text-sm text-gray-700">
        Set your Market Value Rate to raise or lower the Sold ARV and Active ARV you see on the Decision Making page.
        Use a positive rate if values in your market are running higher than REIX shows, or a negative rate if they are
        running lower. Your max bid and returns are calculated from the adjusted value. Decisions you have already saved
        are not changed.
      </p>

      <div className="mt-8 sm:max-w-md">
        <label htmlFor="market_value_rate" className="block text-sm font-medium leading-6 text-gray-900">
          Market Value Rate (%)
        </label>

        <input
          id="market_value_rate_slider"
          name="market_value_rate_slider"
          type="range"
          aria-label="Market Value Rate slider"
          min={MIN_MARKET_VALUE_RATE}
          max={MAX_MARKET_VALUE_RATE}
          step={0.25}
          disabled={isLoading}
          value={parsedRate ?? DEFAULT_MARKET_VALUE_RATE}
          onChange={(e) => handleChange(e.target.value)}
          className="mt-3 w-full accent-indigo-600"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>{MIN_MARKET_VALUE_RATE}%</span>
          <span>0%</span>
          <span>+{MAX_MARKET_VALUE_RATE}%</span>
        </div>

        <div className="mt-4 sm:max-w-xs">
          <input
            id="market_value_rate"
            name="market_value_rate"
            type="text"
            inputMode="decimal"
            placeholder="0"
            disabled={isLoading}
            value={rateInput}
            onChange={(e) => handleChange(e.target.value)}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        <p className={classNames('mt-2 text-sm', isInvalid ? 'text-red-600' : 'text-gray-500')}>
          Enter a rate from {MIN_MARKET_VALUE_RATE}% to +{MAX_MARKET_VALUE_RATE}% with up to two decimals, for example -1.25.
        </p>
        {parsedRate !== null && (
          <p className="mt-2 text-sm text-gray-500">
            At {formatMarketValueRate(parsedRate)}, a $400,000 REIX value would show as ${Math.round(exampleValue).toLocaleString('en-US')}.
          </p>
        )}
        {saveError && <p className="mt-2 text-sm text-red-600">Could not save your Market Value Rate. Please try again.</p>}
        {saved && <p className="mt-2 text-sm text-green-600">Market Value Rate saved.</p>}
      </div>

      <div className="mt-6 flex gap-x-3">
        <button
          type="button"
          disabled={isLoading || isSaving || parsedRate === null}
          onClick={handleSave}
          className={classNames(
            'rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
            isLoading || isSaving || parsedRate === null ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-500 hover:cursor-pointer'
          )}
        >
          Save
        </button>
        <button
          type="button"
          disabled={isLoading || isSaving}
          onClick={() => handleChange(String(DEFAULT_MARKET_VALUE_RATE))}
          className="rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:cursor-pointer"
        >
          Reset to REIX value (0%)
        </button>
      </div>
    </div>
  );
};

export default ValuationSettingsPage;
